import { useState, useEffect } from 'react';

// Simple in-memory cache
const cache: Record<string, unknown> = {};
const pendingRequests: Record<string, Promise<unknown>> = {};

// Preload critical data files on app start
const PRELOAD_URLS = [
  '/data/services.json',
  '/data/services-detail.json',
  '/data/team.json',
  '/data/industries.json',
  '/data/impact.json',
  '/data/how-it-works.json',
  '/data/why-eco-enviro.json',
  '/data/presence.json',
  '/data/certifications.json',
  '/data/about.json',
];

// Preload function - call once on app initialization
export function preloadJsonData(): void {
  PRELOAD_URLS.forEach((url) => {
    if (!cache[url] && !pendingRequests[url]) {
      pendingRequests[url] = fetch(url)
        .then((res) => res.json())
        .then((data) => {
          cache[url] = data;
          delete pendingRequests[url];
          return data;
        })
        .catch(() => {
          delete pendingRequests[url];
        });
    }
  });
}

// Start preloading immediately when module loads
if (typeof window !== 'undefined') {
  // Use requestIdleCallback for non-blocking preload
  if ('requestIdleCallback' in window) {
    (window as Window).requestIdleCallback(() => preloadJsonData(), { timeout: 1000 });
  } else {
    setTimeout(preloadJsonData, 100);
  }
}

export function useJsonData<T>(url: string): { data: T | null; loading: boolean; error: Error | null } {
  // Always start with null/true to ensure consistent SSR/client hydration
  // The cache check happens in useEffect after hydration
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Check cache first after mount
    if (cache[url]) {
      setData(cache[url] as T);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchData() {
      try {
        // Check if there's already a pending request
        let result: unknown;
        if (pendingRequests[url]) {
          result = await pendingRequests[url];
        } else {
          // Create new request
          pendingRequests[url] = fetch(url).then((res) => {
            if (!res.ok) throw new Error(`Failed to fetch ${url}`);
            return res.json();
          });
          result = await pendingRequests[url];
          cache[url] = result;
          delete pendingRequests[url];
        }

        if (!cancelled) {
          setData(result as T);
          setLoading(false);
        }
      } catch (err) {
        delete pendingRequests[url];
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { data, loading, error };
}
